import {
	CalendarBlank,
	ChatCircle,
	NotePencil,
	ShareNetwork,
} from "@phosphor-icons/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	ChevronsUpDown,
	Image as ImageIcon,
	Plus,
	TrendingUp,
	Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LogoIcon } from "@/components/brand/logo-icon";
import { FaqSection } from "@/components/faq-section";
import { GoogleOneTap } from "@/components/google-one-tap";
import { HeroBeam } from "@/components/hero-beam";
import { PricingTable } from "@/components/pricing-table";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { HomepageAgentInstall } from "@/components/setup/homepage-agent-install";
import {
	CloudRainIcon,
	type CloudRainIconHandle,
} from "@/components/ui/cloud-rain";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import { RadioTowerIcon } from "@/components/ui/radio-tower";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import {
	SparklesIcon,
	type SparklesIconHandle,
} from "@/components/ui/sparkles";
import { TrendingUpIcon } from "@/components/ui/trending-up";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const HERO_GRID_CELLS = Array.from({ length: 64 }, (_, index) => {
	const row = Math.floor(index / 8) + 1;
	const column = (index % 8) + 1;
	return {
		id: `hero-grid-cell-${row}-${column}`,
		row,
		column,
		isCoveredByHero: row >= 2 && row <= 7 && column >= 2 && column <= 7,
	};
}).filter((cell) => !cell.isCoveredByHero);
const HOW_IT_WORKS_ID = "how-it-works";

export const Route = createFileRoute("/")({
	component: HomePage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/" });

		return {
			links: [
				{
					rel: "preload",
					as: "image",
					href: "/showcase-bg.webp",
					fetchPriority: "low",
				},
				...buildLocalizedSeoLinks({ matches, internalPath: "/" }),
			],
			meta: [
				{ title: "AgentReacher | Connect your AI agent to social media." },
				{
					name: "description",
					content:
						"Compose once, schedule, and publish across Instagram, Facebook, LinkedIn, X, TikTok, YouTube, Pinterest, Threads, and Bluesky from one chat with your AI agent.",
				},
				{
					property: "og:title",
					content: "AgentReacher | Connect your AI agent to social media.",
				},
				{
					property: "og:description",
					content:
						"Compose once, schedule, and publish across every social platform from one chat with your AI agent.",
				},
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentReacher" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
		};
	},
});

function HomePage() {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<GoogleOneTap />
			<PublicHeader />

			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						{/* Hero */}
						<div className="relative overflow-hidden rounded-md bg-white dark:bg-black [--hero-row-size:4.75rem] sm:bg-border dark:sm:bg-border sm:[--hero-row-size:5.25rem]">
							<div className="relative sm:grid sm:grid-cols-8 sm:grid-rows-[repeat(8,minmax(var(--hero-row-size),auto))] sm:gap-px">
								{HERO_GRID_CELLS.map((cell) => (
									<div
										key={cell.id}
										aria-hidden="true"
										className="pointer-events-none hidden rounded-md bg-white dark:bg-black sm:block"
										style={{
											gridColumn: cell.column,
											gridRow: cell.row,
										}}
									/>
								))}
								<div className="w-full homepage-hover-cell homepage-hero-cell relative mx-auto flex max-w-[calc(100%-2px)] flex-col items-center justify-center rounded-md bg-white dark:bg-black px-5 py-9 text-center sm:col-start-2 sm:col-end-8 sm:row-start-2 sm:row-end-8 sm:max-w-none sm:px-10 sm:py-12">
									<Reveal className="flex flex-col items-center">
										<h1 className="text-[3rem] font-semibold leading-[0.95] tracking-[-0.05em] sm:max-w-none sm:text-5xl sm:leading-[1.1] sm:tracking-[-0.04em] lg:text-6xl">
											<span className="block sm:inline">Run your </span>
											<span className="block sm:inline">
												social calendar from{" "}
											</span>
											<span className="inline-flex items-center whitespace-nowrap">
												<span className="text-blue-600">one chat.</span>
												<HeroChatIcon />
											</span>
										</h1>
										<p className="mx-auto mt-3 max-w-[18rem] text-base leading-relaxed text-muted-foreground sm:max-w-lg sm:text-xl">
											Connect AgentReacher, describe what you want to post, and
											it drafts, schedules and publishes across every social
											platform.
										</p>
										<div className="mt-8 flex w-full max-w-[17rem] flex-col items-center justify-center gap-3 sm:max-w-none sm:flex-row">
											<Link
												to="/choose-plan"
												search={{ redirect: undefined, reason: undefined }}
												className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-[15px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
											>
												Start now
												<ArrowRight className="size-3.5" />
											</Link>
											<a
												href={`#${HOW_IT_WORKS_ID}`}
												className="inline-flex w-full items-center justify-center rounded-md border border-border bg-background px-6 py-3 text-[15px] font-medium text-foreground shadow-sm transition-colors hover:bg-accent sm:w-auto"
											>
												See how it works
											</a>
										</div>

										<HeroPlatformStrip />
									</Reveal>
								</div>
							</div>
						</div>

						<HomepageAgentInstall
							id={HOW_IT_WORKS_ID}
							className="homepage-hover-cell scroll-mt-20"
						/>

						{/* Showcase */}
						<div className="homepage-hover-cell rounded-md bg-white dark:bg-black">
							<Reveal>
								<InteractiveShowcase />
							</Reveal>
						</div>

						{/* Social proof text */}
						<div className="homepage-hover-cell rounded-md bg-white dark:bg-black px-5 py-6 sm:px-8 text-center">
							<Reveal>
								<p className="text-sm text-muted-foreground">
									From the team behind{" "}
									<strong className="font-semibold text-foreground">
										BrandReel
									</strong>{" "}
									and{" "}
									<strong className="font-semibold text-foreground">
										AgentMRR
									</strong>
								</p>
							</Reveal>
						</div>

						{/* Social proof logos */}
						<div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2">
							<a
								href="https://brandreel.com"
								target="_blank"
								rel="noopener noreferrer"
								className="homepage-hover-cell flex items-center justify-center gap-2 rounded-md bg-white dark:bg-black px-5 py-8 sm:px-8"
							>
								<Reveal className="flex items-center justify-center gap-2">
									<svg
										aria-hidden="true"
										className="h-7 w-auto min-w-7"
										viewBox="8 8 32 32"
										width="32"
										height="32"
										fill="currentColor"
									>
										<g transform="translate(24,24)">
											<path d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z" />
											<path
												d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z"
												transform="rotate(60)"
											/>
											<path
												d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z"
												transform="rotate(120)"
											/>
											<path
												d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z"
												transform="rotate(180)"
											/>
											<path
												d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z"
												transform="rotate(240)"
											/>
											<path
												d="M0,-14 A14,14,0,0,1,11.03,-8.62 L4.57,-2.03 A5,5,0,0,0,1.21,-4.85Z"
												transform="rotate(300)"
											/>
										</g>
									</svg>
									<span className="text-[22px] font-extrabold text-foreground">
										BrandReel
									</span>
								</Reveal>
							</a>
							<a
								href="https://agentmrr.com"
								target="_blank"
								rel="noopener noreferrer"
								className="homepage-hover-cell flex items-center justify-center gap-2 rounded-md bg-white dark:bg-black px-5 py-8 sm:px-8"
							>
								<Reveal
									delay={80}
									className="flex items-center justify-center gap-2"
								>
									<img
										src="/agentmrr-logo-dark.svg"
										alt=""
										aria-hidden="true"
										width="33"
										height="28"
										className="h-7 w-auto min-w-7 block dark:hidden"
									/>
									<img
										src="/agentmrr-logo-light.svg"
										alt=""
										aria-hidden="true"
										width="33"
										height="28"
										className="h-7 w-auto min-w-7 hidden dark:block"
									/>
									<span className="text-[22px] font-extrabold text-foreground">
										AgentMRR
									</span>
								</Reveal>
							</a>
						</div>

						{/* Workflow heading */}
						<div className="homepage-hover-cell rounded-md bg-white dark:bg-black">
							<Reveal className="px-5 py-10 sm:px-8 sm:py-12">
								<div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
									<TrendingUp className="size-3.5" />
									What changes
								</div>
								<h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
									Less posting work. More consistent{" "}
									<span className="text-blue-600 dark:text-blue-400">
										presence
									</span>
									.
								</h2>
								<p
									className="mt-2 max-w-xl text-base text-muted-foreground"
									style={{ lineHeight: "1.6" }}
								>
									AgentReacher turns ideas into scheduled content, keeps the
									calendar moving, and helps you show up without bouncing
									between apps.
								</p>
							</Reveal>
						</div>

						{/* Pain vs Gain comparison */}
						<div className="grid grid-cols-1 gap-px bg-border md:grid-cols-2">
							<div className="homepage-hover-cell rounded-md bg-white dark:bg-black">
								<PainColumn />
							</div>
							<div className="homepage-hover-cell rounded-md bg-white dark:bg-black">
								<GainColumn />
							</div>
						</div>

						{/* Bento Row 3: All channels, full width */}
						<div className="homepage-hover-cell @container rounded-md bg-white dark:bg-black">
							<Reveal>
								<ChannelsMockup />
							</Reveal>
						</div>

						{/* Row 3: beam + text */}
						<div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-3">
							<Reveal className="homepage-hover-cell col-span-1 flex flex-col justify-center rounded-md bg-white dark:bg-black px-5 py-6 sm:col-span-2 sm:p-8">
								<div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
									<Zap className="size-3.5" />
									Inbound, on autopilot
								</div>
								<p className="mt-3 text-xl font-semibold leading-snug tracking-[-0.03em] sm:text-2xl">
									AgentReacher publishes across all channels while you sleep,{" "}
									<span className="text-muted-foreground font-normal">
										turning consistent presence into profile visits, DMs, and
										signups.
									</span>
								</p>
							</Reveal>
							<div
								className="homepage-hover-cell flex items-center justify-center rounded-md bg-white dark:bg-black p-6"
								style={{
									backgroundImage:
										"radial-gradient(ellipse 90% 80% at 50% 50%, rgba(15,10,30,0.06) 0%, transparent 70%)",
								}}
							>
								<Reveal delay={120}>
									<HeroBeam />
								</Reveal>
							</div>
						</div>

						{/* Outcomes grid */}
						<div className="bg-border">
							<div className="grid grid-cols-1 gap-px md:grid-cols-3">
								{OUTCOMES.map((outcome, index) => (
									<div
										key={outcome.title}
										className="homepage-hover-cell group rounded-md bg-white dark:bg-black"
									>
										<Reveal delay={(index % 3) * 80} className="h-full">
											<OutcomeCell {...outcome} />
										</Reveal>
									</div>
								))}
							</div>
						</div>

						{/* Founder */}
						<section className="homepage-hover-cell rounded-md bg-white dark:bg-black px-5 py-12 sm:px-8 sm:py-16 lg:px-12">
							<Reveal className="mx-auto max-w-6xl">
								<div className="grid gap-8 md:grid-cols-[minmax(260px,340px)_1fr] md:items-start md:gap-16 lg:gap-20">
									<Reveal>
										<div className="flex flex-col items-start gap-5">
											<div className="relative aspect-[4/5] w-full shrink-0 overflow-hidden rounded-xl bg-muted shadow-[0_1px_2px_hsl(var(--foreground)/0.06),0_12px_32px_-12px_hsl(var(--foreground)/0.18)]">
												<img
													src="/thijs-founder.webp?v=20260513"
													alt="Thijs Smudde, founder of AgentReacher"
													loading="lazy"
													decoding="async"
													className="absolute inset-0 h-full w-full object-cover"
													style={{ objectPosition: "78% 28%" }}
												/>
											</div>
											<div className="text-sm leading-tight">
												<div className="font-medium text-foreground">
													Thijs Smudde
												</div>
												<div className="mt-0.5 text-muted-foreground">
													Founder, AgentReacher
												</div>
											</div>
										</div>
									</Reveal>
									<Reveal delay={120}>
										<h2 className="text-2xl font-semibold tracking-[-0.04em] text-foreground sm:text-3xl">
											One tool to run every account, every platform, every day.
										</h2>
										<div
											className="mt-6 space-y-4 text-[15px] text-muted-foreground"
											style={{ lineHeight: "1.65" }}
										>
											<p>
												I built AgentReacher because social media management
												should not come at the cost of running your business.
												Connect every account once and publish to Instagram,
												Facebook, LinkedIn, X, TikTok, YouTube, Pinterest,
												Threads, and Bluesky from one place.
											</p>
											<p>
												Cross-post in one click. Auto-reply to your own posts
												from your own account or any brand account you manage.
												Set auto-plugs that drop a follow-up comment the moment
												a post crosses an engagement threshold. Reply to
												mentions the second they land.
											</p>
											<p>
												Analytics tell you what's working. Social listening
												tells you what to say next. Everything runs from one
												chat with your AI, so the busywork disappears and you
												stay focused on the posts that actually move the needle.
											</p>
										</div>
									</Reveal>
								</div>
							</Reveal>
						</section>

						{/* Pricing heading */}
						<section className="homepage-hover-cell rounded-md bg-white dark:bg-black px-5 py-12 sm:px-8 sm:py-14">
							<Reveal className="space-y-2">
								<h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
									Pick Pro or Max.
								</h2>
								<p className="text-base text-muted-foreground">
									Pro fits steady solo publishing. Max is built for teams
									running multiple brands.
								</p>
							</Reveal>
						</section>

						{/* Pricing table */}
						<PricingTable />

						<FaqSection />
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

const PLATFORMS = [
	{
		name: "Instagram",
		icon: InstagramIcon,
		bg: "bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]",
		glow: "shadow-[0_0_18px_rgba(214,36,159,0.45)]",
	},
	{
		name: "Facebook",
		icon: FacebookIcon,
		bg: "bg-[#1877F2]",
		glow: "shadow-[0_0_18px_rgba(24,119,242,0.45)]",
	},
	{
		name: "LinkedIn",
		icon: LinkedInIcon,
		bg: "bg-[#0A66C2]",
		glow: "shadow-[0_0_18px_rgba(10,102,194,0.45)]",
	},
	{
		name: "X",
		icon: XIcon,
		bg: "bg-black",
		glow: "shadow-[0_0_18px_rgba(0,0,0,0.35)] dark:shadow-[0_0_18px_rgba(255,255,255,0.18)]",
	},
	{
		name: "TikTok",
		icon: TikTokIcon,
		bg: "bg-black",
		glow: "shadow-[0_0_18px_rgba(0,242,234,0.35)]",
	},
	{
		name: "YouTube",
		icon: YouTubeIcon,
		bg: "bg-[#FF0000]",
		glow: "shadow-[0_0_18px_rgba(255,0,0,0.45)]",
	},
	{
		name: "Pinterest",
		icon: PinterestIcon,
		bg: "bg-[#BD081C]",
		glow: "shadow-[0_0_18px_rgba(189,8,28,0.45)]",
	},
	{
		name: "Threads",
		icon: ThreadsIcon,
		bg: "bg-black",
		glow: "shadow-[0_0_18px_rgba(0,0,0,0.35)] dark:shadow-[0_0_18px_rgba(255,255,255,0.18)]",
	},
	{
		name: "Bluesky",
		icon: BlueskyIcon,
		bg: "bg-[#0085FF]",
		glow: "shadow-[0_0_18px_rgba(0,133,255,0.45)]",
	},
];

function HeroChatIcon() {
	return (
		<span
			aria-hidden="true"
			className="relative ml-4 mr-0.5 inline-block h-9 w-9 shrink-0 -rotate-[6deg] overflow-hidden rounded-[22%] bg-white align-middle shadow-[0_3px_10px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.06)] [perspective:600px] sm:h-[1.1em] sm:w-[1.1em] lg:mr-0 lg:h-14 lg:w-14"
		>
			<span className="absolute inset-[14%] animate-logo-cycle-1 [backface-visibility:hidden]">
				<OpenAIMark className="h-full w-full" />
			</span>
			<span className="absolute inset-[14%] animate-logo-cycle-2 [backface-visibility:hidden]">
				<ClaudeSparkleMark className="h-full w-full" />
			</span>
			<span className="absolute inset-0 animate-logo-cycle-3 [backface-visibility:hidden]">
				<AgentReacherAMark className="h-full w-full" />
			</span>
		</span>
	);
}

function AgentReacherAMark({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 2048 2048"
			width="36"
			height="36"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<g transform="translate(1024 1024) scale(1.3) translate(-1026.5 -939)">
				<path
					fill="#2563EB"
					d="M 901.777 497.477 C 970.874 498.149 1040.32 496.109 1109.47 496.921 C 1172.11 497.656 1242.56 490.79 1298.82 523.061 C 1342.48 548.104 1371.67 593.196 1383.85 641.905 C 1405.57 728.813 1405.55 834.511 1362.13 914.992 C 1335.82 961.831 1290.2 993.401 1241.23 1011.56 C 1227.26 1016.74 1214.18 1022.57 1199.55 1026.49 C 1203.04 1031.54 1207.27 1035.76 1211.32 1040.39 C 1255.02 1090.3 1298.36 1140.65 1342.19 1190.45 L 1392.48 1247.81 C 1400.3 1256.69 1422.91 1277.68 1422.12 1290.5 C 1421.74 1296.79 1408.54 1299.62 1402.87 1299.78 C 1378.3 1300.44 1353.51 1299.91 1328.85 1299.94 C 1303.82 1300.03 1278.8 1299.99 1253.78 1299.82 C 1237.05 1299.66 1218.32 1300.02 1201.97 1296.84 C 1194.42 1295.37 1191.41 1291.82 1186.33 1285.86 C 1166.19 1262.27 1146.56 1237.74 1127.03 1213.64 L 1018.13 1079.44 L 949.644 995.172 C 943.726 987.997 904.865 943.826 906.839 936.769 C 918.456 930.02 948.078 923.709 962.709 919.651 C 989.277 912.241 1015.79 904.639 1042.25 896.845 C 1081.36 887.431 1148.39 864.607 1177.48 837.033 C 1199.33 816.289 1209.08 792.263 1210.07 762.521 C 1212.1 701.516 1188.68 707.963 1140.29 707.951 L 1074.44 707.977 L 865.152 708.011 L 865.29 1089.48 L 865.349 1208.71 C 865.39 1228.35 865.749 1248.21 865.456 1267.66 C 865.239 1282.06 859.626 1288.21 847.668 1294.55 C 833.561 1302.03 818.537 1309.03 804.211 1315.95 C 777.597 1328.94 750.881 1341.72 724.067 1354.28 C 704.183 1363.66 683.727 1375.2 663.035 1381.62 C 649.739 1385.75 635.16 1377.58 634.45 1363.45 C 633.365 1341.85 633.891 1319.27 633.887 1297.66 L 633.793 1154.57 L 632.384 885.084 C 631.87 828.732 631.487 772.358 631.333 716.012 C 631.289 699.687 638.56 693.328 654.401 691.386 C 671.958 689.064 689.907 690.483 707.528 689.907 C 743.279 688.737 781.027 690.514 816.538 687.636 C 819.617 685.486 822.563 682.608 825.359 680.054 C 831.682 660.78 827.962 573.453 828.038 547.297 C 828.223 483.162 835.364 498.997 901.777 497.477 z"
				/>
			</g>
		</svg>
	);
}

function ClaudeSparkleMark({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 256 257"
			width="36"
			height="36"
			xmlns="http://www.w3.org/2000/svg"
			preserveAspectRatio="xMidYMid meet"
			className={className}
		>
			<path
				fill="#D97757"
				d="m50.228 170.321 50.357-28.257.843-2.463-.843-1.361h-2.462l-8.426-.518-28.775-.778-24.952-1.037-24.175-1.296-6.092-1.297L0 125.796l.583-3.759 5.12-3.434 7.324.648 16.202 1.101 24.304 1.685 17.629 1.037 26.118 2.722h4.148l.583-1.685-1.426-1.037-1.101-1.037-25.147-17.045-27.22-18.017-14.258-10.37-7.713-5.25-3.888-4.925-1.685-10.758 7-7.713 9.397.649 2.398.648 9.527 7.323 20.35 15.75L94.817 91.9l3.889 3.24 1.555-1.102.195-.777-1.75-2.917-14.453-26.118-15.425-26.572-6.87-11.018-1.814-6.61c-.648-2.723-1.102-4.991-1.102-7.778l7.972-10.823L71.42 0 82.05 1.426l4.472 3.888 6.61 15.101 10.694 23.786 16.591 32.34 4.861 9.592 2.592 8.879.973 2.722h1.685v-1.556l1.36-18.211 2.528-22.36 2.463-28.776.843-8.1 4.018-9.722 7.971-5.25 6.222 2.981 5.12 7.324-.713 4.73-3.046 19.768-5.962 30.98-3.889 20.739h2.268l2.593-2.593 10.499-13.934 17.628-22.036 7.778-8.749 9.073-9.657 5.833-4.601h11.018l8.1 12.055-3.628 12.443-11.342 14.388-9.398 12.184-13.48 18.147-8.426 14.518.778 1.166 2.01-.194 30.46-6.481 16.462-2.982 19.637-3.37 8.88 4.148.971 4.213-3.5 8.62-20.998 5.184-24.628 4.926-36.682 8.685-.454.324.519.648 16.526 1.555 7.065.389h17.304l32.21 2.398 8.426 5.574 5.055 6.805-.843 5.184-12.962 6.611-17.498-4.148-40.83-9.721-14-3.5h-1.944v1.167l11.666 11.406 21.387 19.314 26.767 24.887 1.36 6.157-3.434 4.86-3.63-.518-23.526-17.693-9.073-7.972-20.545-17.304h-1.36v1.814l4.73 6.935 25.017 37.59 1.296 11.536-1.814 3.76-6.481 2.268-7.13-1.297-14.647-20.544-15.1-23.138-12.185-20.739-1.49.843-7.194 77.448-3.37 3.953-7.778 2.981-6.48-4.925-3.436-7.972 3.435-15.749 4.148-20.544 3.37-16.333 3.046-20.285 1.815-6.74-.13-.454-1.49.194-15.295 20.999-23.267 31.433-18.406 19.702-4.407 1.75-7.648-3.954.713-7.064 4.277-6.286 25.47-32.405 15.36-20.092 9.917-11.6-.065-1.686h-.583L44.07 198.125l-12.055 1.555-5.185-4.86.648-7.972 2.463-2.593 20.35-13.999-.064.065Z"
			/>
		</svg>
	);
}

function OpenAIMark({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			width="36"
			height="36"
			fill="#000000"
			className={className}
		>
			<path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.911 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.182a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.998-2.9 6.056 6.056 0 0 0-.748-7.073zm-9.022 12.608a4.476 4.476 0 0 1-2.876-1.04l.142-.08 4.778-2.759a.778.778 0 0 0 .393-.681v-6.737l2.02 1.169a.072.072 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.495 4.494zm-9.66-4.125a4.471 4.471 0 0 1-.535-3.014l.142.085 4.783 2.758a.771.771 0 0 0 .78 0l5.843-3.369v2.333a.08.08 0 0 1-.033.061l-4.832 2.788a4.499 4.499 0 0 1-6.141-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.677l5.814 3.354-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.596 3.856L13.103 8.36l2.016-1.16a.076.076 0 0 1 .07 0l4.831 2.79a4.494 4.494 0 0 1-.677 8.105v-5.677a.79.79 0 0 0-.407-.667zm2.011-3.023-.142-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.062l4.83-2.786a4.499 4.499 0 0 1 6.68 4.66zM8.307 12.863l-2.02-1.164a.08.08 0 0 1-.038-.057V6.074a4.499 4.499 0 0 1 7.376-3.453l-.142.08-4.778 2.758a.778.778 0 0 0-.393.682zm1.097-2.366 2.602-1.5 2.607 1.5v3l-2.597 1.5-2.607-1.5z" />
		</svg>
	);
}

function HeroPlatformStrip() {
	return (
		<div className="mt-8 flex w-full flex-col items-center gap-4">
			<p className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
				Post to all your platforms
			</p>
			<div className="flex w-full flex-wrap items-center justify-center gap-2 sm:gap-3">
				{PLATFORMS.map(({ name, icon: Icon, bg }) => (
					<div
						key={name}
						title={name}
						className={`flex size-8 items-center justify-center rounded-full text-white transition-transform hover:-translate-y-0.5 sm:size-9 ${bg}`}
					>
						<Icon className="size-4 sm:size-[18px]" />
					</div>
				))}
			</div>
		</div>
	);
}

type ShowcaseView =
	| "chat"
	| "compose"
	| "connect"
	| "posts"
	| "calendar"
	| "media";

type IconProps = { className?: string };

type ShowcaseNavItem = {
	id: ShowcaseView;
	label: string;
	icon: (props: IconProps) => React.ReactNode;
	activeClass: string;
	inactiveIconClass: string;
	sidebarActiveClass: string;
};

const NAV_ACTIVE_CLASS =
	"border-blue-600 bg-background text-blue-600 dark:border-blue-400 dark:text-blue-400";
const SIDEBAR_ACTIVE_CLASS =
	"bg-sidebar-accent text-sidebar-accent-foreground font-medium";

const SHOWCASE_NAV: ShowcaseNavItem[] = [
	{
		id: "chat",
		label: "Chat",
		icon: (p) => <ChatCircle {...p} weight="duotone" />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
	{
		id: "connect",
		label: "Connect",
		icon: (p) => <ShareNetwork {...p} weight="duotone" />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
	{
		id: "posts",
		label: "Posts",
		icon: (p) => <NotePencil {...p} weight="duotone" />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
	{
		id: "calendar",
		label: "Calendar",
		icon: (p) => <CalendarBlank {...p} weight="duotone" />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
	{
		id: "media",
		label: "Media",
		icon: (p) => <ImageIcon {...p} />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
];

const ACTION_PILLS: ShowcaseNavItem[] = [
	SHOWCASE_NAV[0],
	{
		id: "compose",
		label: "Create",
		icon: (p) => <Plus {...p} />,
		activeClass: NAV_ACTIVE_CLASS,
		inactiveIconClass: "text-muted-foreground",
		sidebarActiveClass: SIDEBAR_ACTIVE_CLASS,
	},
	...SHOWCASE_NAV.slice(1).filter(({ id }) => id !== "media"),
];

const SHOWCASE_SECTIONS: Array<{ label: string; ids: ShowcaseView[] }> = [
	{ label: "Overview", ids: ["chat", "connect"] },
	{ label: "Publish", ids: ["posts", "calendar", "media"] },
];

function getNavItem(id: ShowcaseView): ShowcaseNavItem {
	const item = SHOWCASE_NAV.find((n) => n.id === id);
	if (!item) throw new Error(`Unknown showcase view: ${id}`);
	return item;
}

function InteractiveShowcase() {
	const [view, setView] = useState<ShowcaseView>("chat");

	useEffect(() => {
		const desktopQuery = window.matchMedia("(min-width: 640px)");
		if (desktopQuery.matches) {
			setView("compose");
		}
	}, []);

	return (
		<>
			<div className="relative border-t border-border">
				<SocialShowcase view={view} onChange={setView} />
			</div>
			<ActionPills active={view} onChange={setView} />
		</>
	);
}

function ActionPills({
	active,
	onChange,
}: {
	active: ShowcaseView;
	onChange: (view: ShowcaseView) => void;
}) {
	return (
		<div className="bg-white px-3 py-2 dark:bg-black sm:px-8 sm:py-4">
			<div
				className="mx-auto flex max-w-5xl items-center justify-start gap-2 overflow-x-auto pb-1 [scrollbar-width:none] sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
				role="tablist"
				aria-label="Switch dashboard preview"
			>
				{ACTION_PILLS.map(
					({ id, label, icon: Icon, activeClass, inactiveIconClass }) => {
						const isActive = id === active;
						return (
							<button
								key={id}
								type="button"
								role="tab"
								aria-selected={isActive}
								onClick={() => onChange(id)}
								className={`inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:px-4 sm:py-2 sm:text-sm ${
									isActive
										? activeClass
										: "homepage-hover-cell border-border bg-background text-foreground"
								}`}
							>
								<Icon
									className={`size-3.5 ${isActive ? "" : inactiveIconClass}`}
								/>
								{label}
							</button>
						);
					},
				)}
			</div>
		</div>
	);
}

function SocialShowcase({
	view,
	onChange,
}: {
	view: ShowcaseView;
	onChange: (view: ShowcaseView) => void;
}) {
	const currentLabel = view === "compose" ? "Compose" : getNavItem(view).label;
	return (
		<div
			className="px-3 pt-3 pb-0 sm:px-8 sm:pt-14"
			style={{
				backgroundImage:
					"radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(rgba(8,10,20,0.55), rgba(8,10,20,0.55)), url('/showcase-bg.webp')",
				backgroundSize: "14px 14px, 100% 100%, cover",
				backgroundPosition: "0 0, center, center",
				backgroundRepeat: "repeat, no-repeat, no-repeat",
			}}
		>
			<div className="homepage-hover-cell mx-auto max-w-4xl overflow-hidden rounded-t-xl border border-b-0 border-border bg-card">
				{/* Mac browser chrome */}
				<div className="relative flex h-7 items-center border-b border-border bg-muted/40 px-2.5 sm:h-8 sm:px-3">
					<div className="flex gap-1.5">
						<span className="size-2.5 rounded-full bg-muted-foreground/30" />
						<span className="size-2.5 rounded-full bg-muted-foreground/30" />
						<span className="size-2.5 rounded-full bg-muted-foreground/30" />
					</div>
					<span className="absolute left-1/2 max-w-[60%] -translate-x-1/2 truncate text-center text-xs font-medium text-muted-foreground sm:max-w-none">
						<span className="hidden sm:inline">AgentReacher - </span>
						{currentLabel}
					</span>
				</div>

				<div className="grid grid-cols-[56px_1fr] sm:grid-cols-[210px_1fr]">
					{/* Sidebar */}
					<aside className="flex flex-col border-r border-sidebar-border bg-sidebar p-2 sm:p-3">
						<div className="mb-3 flex items-center gap-2 px-1.5 py-1 sm:px-2">
							<LogoIcon className="size-7 shrink-0 text-foreground" />
							<span className="hidden truncate text-sm font-semibold tracking-tight sm:inline">
								AgentReacher
							</span>
						</div>

						<div className="mb-3 hidden flex-col gap-1 sm:flex">
							<p className="px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
								Workspace
							</p>
							<div className="flex items-center gap-2 rounded-md border border-sidebar-border bg-background px-2 py-1.5">
								<div className="min-w-0 flex-1">
									<div className="truncate text-[12px] font-medium leading-tight text-foreground">
										BrandReel
									</div>
									<div className="truncate text-[9px] uppercase tracking-wide text-muted-foreground leading-tight">
										Owner
									</div>
								</div>
								<ChevronsUpDown className="size-3 shrink-0 text-muted-foreground" />
							</div>
						</div>

						<p className="mb-1 hidden px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:block">
							Action
						</p>
						<button
							type="button"
							onClick={() => onChange("compose")}
							aria-label="Create"
							aria-current={view === "compose" ? "page" : undefined}
							className="mb-3 flex items-center justify-center gap-2 rounded-md bg-sidebar-primary px-2 py-1.5 text-sm font-medium text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90 sm:justify-start"
						>
							<Plus aria-hidden="true" className="size-3.5 shrink-0" />
							<span className="hidden truncate sm:inline">Create</span>
						</button>

						{SHOWCASE_SECTIONS.map((section) => (
							<div key={section.label} className="mb-3 flex flex-col gap-1">
								<p className="hidden px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:block">
									{section.label}
								</p>
								{section.ids.map((id) => {
									const item = getNavItem(id);
									const Icon = item.icon;
									const isActive = id === view;
									return (
										<button
											key={id}
											type="button"
											onClick={() => onChange(id)}
											aria-label={item.label}
											aria-current={isActive ? "page" : undefined}
											className={`flex cursor-pointer items-center justify-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors sm:justify-start ${
												isActive
													? item.sidebarActiveClass
													: "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground"
											}`}
										>
											<Icon aria-hidden="true" className="size-4 shrink-0" />
											<span className="hidden truncate sm:inline">
												{item.label}
											</span>
										</button>
									);
								})}
							</div>
						))}
					</aside>

					{/* Main */}
					<div className="min-w-0">
						<div className="h-[560px] overflow-hidden p-4 sm:h-[520px] sm:p-6">
							<ShowcaseBody view={view} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function ShowcaseBody({ view }: { view: ShowcaseView }) {
	switch (view) {
		case "chat":
			return <ChatView />;
		case "compose":
			return <ComposeView />;
		case "connect":
			return <ConnectView />;
		case "posts":
			return <PostsView />;
		case "calendar":
			return <CalendarView />;
		case "media":
			return <MediaView />;
	}
}

const CONNECT_PLATFORM_STYLES: Record<
	string,
	{ iconBg: string; iconColor: string }
> = {
	Instagram: { iconBg: "bg-pink-500/10", iconColor: "text-pink-500" },
	Facebook: { iconBg: "bg-[#1877F2]/10", iconColor: "text-[#1877F2]" },
	LinkedIn: { iconBg: "bg-blue-600/10", iconColor: "text-blue-600" },
	X: { iconBg: "bg-foreground/10", iconColor: "text-foreground" },
	TikTok: { iconBg: "bg-foreground/10", iconColor: "text-foreground" },
	YouTube: { iconBg: "bg-red-500/10", iconColor: "text-red-500" },
	Pinterest: { iconBg: "bg-red-600/10", iconColor: "text-red-600" },
	Threads: { iconBg: "bg-neutral-500/10", iconColor: "text-foreground" },
	Bluesky: { iconBg: "bg-blue-500/10", iconColor: "text-blue-500" },
};

function ConnectView() {
	const connected: Record<string, string> = {
		Instagram: "@brandreel",
		Facebook: "BrandReel",
		LinkedIn: "BrandReel",
		X: "@BrandReel",
		TikTok: "@brandreel",
		YouTube: "BrandReel",
		Pinterest: "@brandreel",
		Threads: "@brandreel",
		Bluesky: "brandreel.bsky.social",
	};
	const connectedCount = Object.keys(connected).length;

	return (
		<div className="flex h-full flex-col gap-3">
			<div className="flex items-start justify-between gap-2 border-b border-border pb-3">
				<div className="min-w-0">
					<p className="truncate text-[15px] font-semibold tracking-tight sm:text-base">
						Connected accounts
					</p>
					<p className="hidden truncate text-[11px] text-muted-foreground sm:block">
						Link one or more accounts per platform so AgentReacher can publish
						on your behalf.
					</p>
				</div>
				<span className="shrink-0 text-[11px] text-muted-foreground">
					{connectedCount} of {PLATFORMS.length} platforms connected
				</span>
			</div>

			<div className="divide-y divide-border rounded-md border border-border bg-card">
				{PLATFORMS.map(({ name, icon: Icon }) => {
					const styles = CONNECT_PLATFORM_STYLES[name] ?? {
						iconBg: "bg-foreground/10",
						iconColor: "text-foreground",
					};
					const handle = connected[name] ?? null;
					return (
						<div
							key={name}
							className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-2.5 py-2 sm:px-3"
						>
							<div
								className={`flex size-7 shrink-0 items-center justify-center rounded-md ${styles.iconBg}`}
							>
								<Icon className={`size-3.5 ${styles.iconColor}`} />
							</div>
							<span className="inline-flex h-7 w-[140px] shrink-0 items-center justify-center rounded-md bg-secondary px-2.5 text-[11px] font-medium text-secondary-foreground">
								Connect {name}
							</span>
							{handle && (
								<span className="inline-flex min-w-0 shrink-0 items-center gap-1 rounded-full border border-border bg-background py-0.5 pl-1.5 pr-0.5 text-[11px] text-foreground">
									<span className="flex size-3.5 shrink-0 items-center justify-center">
										<Icon className={`size-2.5 ${styles.iconColor}`} />
									</span>
									<span className="max-w-[120px] truncate">{handle}</span>
									<span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground">
										<span className="text-[9px] leading-none">×</span>
									</span>
								</span>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

function ChatView() {
	const connectPills: Array<{
		platform: "instagram" | "linkedin" | "x";
		label: string;
	}> = [
		{ platform: "x", label: "Connect X" },
		{ platform: "linkedin", label: "Connect LinkedIn" },
		{ platform: "instagram", label: "Connect Instagram" },
	];

	return (
		<div className="flex h-full flex-col">
			<div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
				<div className="space-y-2">
					<h2 className="max-w-md text-balance text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
						What do you want to post?
					</h2>
					<p className="max-w-sm text-pretty text-xs text-muted-foreground sm:text-sm">
						Draft, schedule, publish, edit social posts from one chat.
					</p>
				</div>

				<div className="homepage-hover-cell flex w-full max-w-md items-center gap-2 rounded-full border border-border/70 bg-muted/55 px-2 py-1.5 shadow-[0_16px_48px_hsl(var(--foreground)/0.08)]">
					<span className="flex size-8 items-center justify-center rounded-full text-muted-foreground sm:size-9">
						<Plus className="size-4" />
					</span>
					<span className="flex-1 truncate text-left text-xs text-muted-foreground/80 sm:text-sm">
						Ask me anything...
					</span>
					<span className="flex size-8 items-center justify-center rounded-full bg-foreground text-background sm:size-9">
						<ArrowRight className="size-3.5 -rotate-90" />
					</span>
				</div>

				<div className="flex w-full max-w-md flex-wrap justify-center gap-1.5">
					{connectPills.map((pill) => (
						<span
							key={pill.platform}
							className="homepage-hover-cell inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
						>
							<span className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-muted">
								{pill.platform === "x" && (
									<XIcon className="size-2 text-foreground" />
								)}
								{pill.platform === "linkedin" && (
									<LinkedInIcon className="size-2 text-[#0A66C2]" />
								)}
								{pill.platform === "instagram" && (
									<InstagramIcon className="size-2 text-[#d6249f]" />
								)}
							</span>
							{pill.label}
						</span>
					))}
				</div>
			</div>
		</div>
	);
}

function ComposeView() {
	const accounts: Array<{
		platform: "x" | "linkedin" | "instagram" | "facebook";
		handle: string;
		selected: boolean;
	}> = [
		{ platform: "x", handle: "@BrandReel", selected: true },
		{ platform: "linkedin", handle: "BrandReel", selected: true },
		{ platform: "instagram", handle: "@brandreel", selected: true },
		{ platform: "facebook", handle: "BrandReel", selected: false },
	];

	const platformBg = {
		linkedin: "bg-[#0A66C2]",
		instagram:
			"bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]",
		x: "bg-black",
		facebook: "bg-[#1877F2]",
	} as const;

	return (
		<div className="grid h-full min-h-0 grid-cols-1 gap-3 sm:grid-cols-[1fr_180px]">
			<div className="flex min-h-0 flex-col gap-3">
				<div className="border-b border-border pb-2">
					<div className="flex items-center gap-1.5 text-[14px] font-medium text-foreground sm:text-[15px]">
						<ImageIcon
							aria-hidden="true"
							className="size-4 text-muted-foreground"
						/>
						Create image post
					</div>
					<p className="hidden truncate text-[11px] text-muted-foreground sm:block">
						Draft a post, add media, pick targets, and schedule.
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-1">
					{accounts.map((acc) => (
						<span
							key={acc.platform}
							className={`inline-flex h-6 items-center gap-1 rounded-full border py-0.5 pl-1 pr-2 text-[10px] font-medium ${
								acc.selected
									? "border-blue-500/40 bg-blue-500/10 text-foreground"
									: "border-border bg-background text-muted-foreground opacity-60"
							}`}
						>
							<span
								className={`flex size-3.5 items-center justify-center rounded-full ${platformBg[acc.platform]}`}
							>
								{acc.platform === "x" && (
									<XIcon className="size-2 text-white" />
								)}
								{acc.platform === "linkedin" && (
									<LinkedInIcon className="size-2 text-white" />
								)}
								{acc.platform === "instagram" && (
									<InstagramIcon className="size-2 text-white" />
								)}
								{acc.platform === "facebook" && (
									<FacebookIcon className="size-2 text-white" />
								)}
							</span>
							<span className="hidden truncate sm:inline">{acc.handle}</span>
						</span>
					))}
				</div>

				<div className="homepage-hover-cell relative aspect-[16/9] overflow-hidden rounded-md border border-border bg-muted">
					<img
						src="https://picsum.photos/seed/ar-compose/640/360"
						alt=""
						loading="lazy"
						className="size-full object-cover"
					/>
					<span className="absolute right-1.5 top-1.5 rounded-md border border-border bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground backdrop-blur">
						Replace
					</span>
				</div>

				<div className="homepage-hover-cell rounded-md border border-border bg-background p-2 text-left">
					<p className="text-[11px] leading-relaxed text-foreground">
						We just shipped the thing we've been quietly building for months.
						<br />
						<span className="text-muted-foreground">
							Here's what changes for your posting workflow...
						</span>
					</p>
					<div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
						<span>142 / 280</span>
						<span>Caption</span>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-1">
					{["Schedule", "Per-platform", "First comment"].map((label) => (
						<span
							key={label}
							className="inline-flex h-6 items-center rounded-md border border-border bg-background px-2 text-[10px] font-medium text-muted-foreground"
						>
							{label}
						</span>
					))}
					<span className="ml-auto inline-flex h-6 items-center gap-1 rounded-md bg-blue-600 px-2 text-[10px] font-medium text-white shadow-sm">
						Schedule
					</span>
				</div>
			</div>

			<aside className="hidden min-h-0 flex-col gap-2 border-l border-border pl-3 sm:flex">
				<div className="flex items-center justify-between">
					<p className="text-[11px] font-semibold text-foreground">
						Post previews
					</p>
					<span className="text-[10px] text-muted-foreground">3</span>
				</div>
				<div className="flex flex-col gap-2 overflow-hidden">
					{accounts
						.filter((a) => a.selected)
						.map((acc) => (
							<div
								key={acc.platform}
								className="rounded-md border border-border bg-background p-1.5"
							>
								<div className="mb-1 flex items-center gap-1">
									<span
										className={`flex size-3.5 items-center justify-center rounded-full ${platformBg[acc.platform]}`}
									>
										{acc.platform === "x" && (
											<XIcon className="size-2 text-white" />
										)}
										{acc.platform === "linkedin" && (
											<LinkedInIcon className="size-2 text-white" />
										)}
										{acc.platform === "instagram" && (
											<InstagramIcon className="size-2 text-white" />
										)}
									</span>
									<span className="truncate text-[10px] font-medium">
										{acc.handle}
									</span>
								</div>
								<div className="aspect-[4/3] overflow-hidden rounded bg-muted">
									<img
										src="https://picsum.photos/seed/ar-compose/240/180"
										alt=""
										loading="lazy"
										className="size-full object-cover"
									/>
								</div>
								<p className="mt-1 line-clamp-2 text-[9px] leading-snug text-muted-foreground">
									We just shipped the thing we've been building for months.
								</p>
							</div>
						))}
				</div>
			</aside>
		</div>
	);
}

function PostsView() {
	const filters: Array<{ label: string; count: number; active?: boolean }> = [
		{ label: "All", count: 12, active: true },
		{ label: "Drafts", count: 2 },
		{ label: "Scheduled", count: 7 },
		{ label: "Published", count: 3 },
	];

	const posts: Array<{
		platform: "linkedin" | "instagram" | "x" | "facebook";
		title: string;
		body: string;
		time: string;
		status: "published" | "scheduled" | "draft";
	}> = [
		{
			platform: "x",
			title: "Launch thread",
			body: "We just shipped the thing we've been quietly building for months.",
			time: "2m ago",
			status: "published",
		},
		{
			platform: "linkedin",
			title: "Why we rebuilt our workflow",
			body: "Three lessons from moving our entire team to AI-assisted posting.",
			time: "Tomorrow 09:00",
			status: "scheduled",
		},
		{
			platform: "instagram",
			title: "Behind the scenes",
			body: "A quick look at the team that ships every week.",
			time: "Today 14:00",
			status: "scheduled",
		},
	];

	const statusStyle = {
		published: {
			surface: "border-emerald-500/20 bg-emerald-500/[0.04]",
			badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
			dot: "bg-emerald-500",
		},
		scheduled: {
			surface: "border-blue-500/20 bg-blue-500/[0.04]",
			badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
			dot: "bg-blue-500",
		},
		draft: {
			surface: "border-border bg-muted/25",
			badge: "bg-muted text-muted-foreground",
			dot: "bg-muted-foreground",
		},
	} as const;

	const formatStatus = (s: keyof typeof statusStyle) =>
		s.charAt(0).toUpperCase() + s.slice(1);

	return (
		<div className="flex h-full flex-col gap-3">
			<div className="flex items-start justify-between gap-2 border-b border-border pb-3">
				<div className="min-w-0">
					<p className="truncate text-[15px] font-semibold tracking-tight sm:text-base">
						Posts
					</p>
					<p className="hidden truncate text-[11px] text-muted-foreground sm:block">
						Draft, schedule, and track delivery.
					</p>
				</div>
				<div className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2 text-[11px] font-medium text-white shadow-sm">
					<Plus className="size-3" />
					Create
				</div>
			</div>

			<div className="rounded-md border border-border bg-card p-1.5">
				<div className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
					{filters.map((f) => (
						<span
							key={f.label}
							className={`inline-flex h-6 shrink-0 items-center gap-1 rounded px-2 text-[11px] ${
								f.active ? "bg-blue-600 text-white" : "text-muted-foreground"
							}`}
						>
							<span>{f.label}</span>
							<span
								className={`rounded px-1 text-[10px] tabular-nums ${
									f.active
										? "bg-white/20 text-white"
										: "bg-muted text-muted-foreground"
								}`}
							>
								{f.count}
							</span>
						</span>
					))}
				</div>
			</div>

			<div className="flex flex-1 flex-col gap-2 overflow-hidden">
				<p className="text-[11px] font-semibold text-foreground">Today</p>
				<div className="space-y-1.5">
					{posts.map((post) => {
						const style = statusStyle[post.status];
						return (
							<PostCard
								key={`${post.platform}-${post.title}`}
								platform={post.platform}
								title={post.title}
								body={post.body}
								time={post.time}
								surfaceClass={style.surface}
								status={
									<span
										className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${style.badge}`}
									>
										<span className={`size-1.5 rounded-full ${style.dot}`} />
										{formatStatus(post.status)}
									</span>
								}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

function CalendarView() {
	const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	type Status = "published" | "scheduled" | "draft";
	type Chip = {
		platform: "linkedin" | "instagram" | "x" | "facebook";
		title: string;
		status: Status;
		time: string;
	};
	type Day = {
		d: number;
		dim?: boolean;
		isToday?: boolean;
		chips?: Chip[];
	};
	const days: Day[] = [
		{ d: 26, dim: true },
		{ d: 27, dim: true },
		{ d: 28, dim: true },
		{ d: 29, dim: true },
		{ d: 30, dim: true },
		{ d: 1 },
		{
			d: 2,
			chips: [
				{ platform: "x", title: "Launch", status: "published", time: "09:00" },
			],
		},
		{ d: 3 },
		{ d: 4 },
		{
			d: 5,
			chips: [
				{
					platform: "linkedin",
					title: "Why we rebuilt",
					status: "published",
					time: "10:30",
				},
			],
		},
		{ d: 6 },
		{
			d: 7,
			chips: [
				{
					platform: "instagram",
					title: "BTS reel",
					status: "published",
					time: "14:00",
				},
			],
		},
		{ d: 8 },
		{ d: 9 },
		{
			d: 10,
			isToday: true,
			chips: [
				{
					platform: "x",
					title: "Customer story",
					status: "scheduled",
					time: "09:00",
				},
				{
					platform: "facebook",
					title: "Recap",
					status: "scheduled",
					time: "16:00",
				},
			],
		},
		{ d: 11 },
		{
			d: 12,
			chips: [
				{
					platform: "linkedin",
					title: "Hiring",
					status: "scheduled",
					time: "11:00",
				},
			],
		},
		{ d: 13 },
		{ d: 14 },
		{
			d: 15,
			chips: [
				{
					platform: "instagram",
					title: "Reel",
					status: "draft",
					time: "12:00",
				},
			],
		},
		{ d: 16 },
	];

	const statusChip = {
		published:
			"bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300",
		scheduled:
			"bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-300",
		draft: "bg-muted/60 border-border text-muted-foreground",
	} as const;
	const statusDot = {
		published: "bg-emerald-500",
		scheduled: "bg-blue-500",
		draft: "bg-muted-foreground",
	} as const;
	const platformText = {
		linkedin: "text-[#0A66C2]",
		instagram: "text-[#d6249f]",
		x: "text-foreground",
		facebook: "text-[#1877F2]",
	} as const;

	return (
		<div className="flex h-full flex-col gap-2.5">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-[14px] font-semibold tracking-tight sm:text-[15px]">
					May 2026
				</p>
				<div className="flex items-center gap-1.5">
					<span className="inline-flex h-6 items-center rounded-md border border-border bg-card px-2 text-[10px] font-medium">
						Today
					</span>
					<div className="inline-flex h-6 items-center rounded-md border border-border bg-card text-muted-foreground">
						<span className="px-1.5">‹</span>
						<span className="h-3 w-px bg-border" />
						<span className="px-1.5">›</span>
					</div>
					<div className="inline-flex h-6 items-center rounded-md bg-muted/60 p-0.5 text-[10px] font-medium">
						<span className="rounded-[3px] px-1.5 text-muted-foreground">
							Week
						</span>
						<span className="rounded-[3px] bg-card px-1.5 text-foreground shadow-sm">
							Month
						</span>
					</div>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
				<div className="grid grid-cols-7 border-b border-border bg-muted/30">
					{weekdays.map((w) => (
						<div
							key={w}
							className="border-r border-border px-1 py-1 text-center text-[9px] font-semibold text-muted-foreground last:border-r-0"
						>
							<span className="hidden sm:inline">{w}</span>
							<span className="sm:hidden">{w.charAt(0)}</span>
						</div>
					))}
				</div>
				<div
					className="grid flex-1 grid-cols-7"
					style={{ gridTemplateRows: "repeat(3, minmax(0, 1fr))" }}
				>
					{days.map((day, idx) => (
						<div
							key={`day-${idx}-${day.d}`}
							className={`flex min-h-0 flex-col gap-0.5 border-r border-b border-border p-1 text-[9px] last:border-r-0 ${
								day.isToday ? "bg-blue-500/5" : ""
							} ${day.dim ? "bg-muted/30" : ""}`}
						>
							<span
								className={`text-[10px] font-medium tabular-nums ${
									day.isToday
										? "inline-flex size-4 items-center justify-center rounded-full bg-blue-600 text-[9px] text-white"
										: day.dim
											? "text-muted-foreground/50"
											: "text-muted-foreground"
								}`}
							>
								{day.d}
							</span>
							<div className="flex min-w-0 flex-col gap-0.5">
								{day.chips?.map((chip) => (
									<div
										key={`${idx}-${chip.platform}-${chip.title}`}
										className={`flex items-center gap-1 rounded-md border px-1 py-0.5 text-[9px] leading-tight shadow-sm ${statusChip[chip.status]}`}
									>
										<span
											className={`size-1 shrink-0 rounded-full ${statusDot[chip.status]}`}
										/>
										<span
											className={`flex shrink-0 items-center justify-center ${platformText[chip.platform]}`}
										>
											{chip.platform === "x" && <XIcon className="size-2" />}
											{chip.platform === "linkedin" && (
												<LinkedInIcon className="size-2" />
											)}
											{chip.platform === "instagram" && (
												<InstagramIcon className="size-2" />
											)}
											{chip.platform === "facebook" && (
												<FacebookIcon className="size-2" />
											)}
										</span>
										<span className="hidden min-w-0 flex-1 truncate sm:inline">
											{chip.title}
										</span>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function MediaView() {
	const stats = [
		{ label: "Total assets", value: 124 },
		{ label: "Images", value: 86 },
		{ label: "Videos", value: 38 },
		{ label: "Imported", value: 12 },
	];

	const tiles: Array<{
		id: string;
		src: string;
		isVideo?: boolean;
		duration?: string;
	}> = [
		{ id: "tile-1", src: "/media-preview/lumiere-product.webp" },
		{
			id: "tile-2",
			src: "/media-preview/lumiere-scene1.webp",
			isVideo: true,
			duration: "0:18",
		},
		{ id: "tile-3", src: "/media-preview/lumiere-scene2.webp" },
		{ id: "tile-4", src: "/media-preview/lumiere-scene5.webp" },
		{
			id: "tile-5",
			src: "/media-preview/lumiere-scene3.webp",
			isVideo: true,
			duration: "0:09",
		},
		{ id: "tile-6", src: "/media-preview/lumiere-logo.webp" },
		{ id: "tile-7", src: "/media-preview/lumiere-scene4.webp" },
		{
			id: "tile-8",
			src: "/media-preview/lumiere-product.webp",
			isVideo: true,
			duration: "0:24",
		},
	];

	return (
		<div className="flex h-full flex-col gap-3">
			<div className="flex items-start justify-between gap-2 border-b border-border pb-3">
				<div className="min-w-0">
					<p className="truncate text-[15px] font-semibold tracking-tight sm:text-base">
						Media Library
					</p>
					<p className="hidden truncate text-[11px] text-muted-foreground sm:block">
						Search, preview, and reuse images or videos.
					</p>
				</div>
				<div className="flex shrink-0 items-center gap-1.5">
					<span className="hidden h-7 items-center rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground sm:inline-flex">
						Import URL
					</span>
					<span className="inline-flex h-7 items-center gap-1 rounded-md bg-blue-600 px-2 text-[11px] font-medium text-white shadow-sm">
						<Plus className="size-3" />
						Upload
					</span>
				</div>
			</div>

			<div className="grid grid-cols-4 gap-1.5">
				{stats.map((stat) => (
					<div
						key={stat.label}
						className="homepage-hover-cell rounded-md border border-border bg-muted/20 px-2 py-1.5"
					>
						<p className="truncate text-[9px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
							{stat.label}
						</p>
						<p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
							{stat.value}
						</p>
					</div>
				))}
			</div>

			<div className="grid grid-cols-4 gap-1.5">
				{tiles.map((tile) => (
					<div
						key={tile.id}
						className="homepage-hover-cell relative aspect-square overflow-hidden rounded-md border border-border bg-muted"
					>
						<img
							src={tile.src}
							alt=""
							loading="lazy"
							className="size-full object-cover"
						/>
						{tile.isVideo && (
							<>
								<span className="pointer-events-none absolute inset-0 bg-black/15" />
								<span className="absolute left-1 top-1 inline-flex size-4 items-center justify-center rounded bg-black/60 text-white">
									<svg
										viewBox="0 0 16 16"
										aria-hidden="true"
										className="size-2.5 fill-white"
									>
										<path d="M5 3.5v9l8-4.5z" />
									</svg>
								</span>
								<span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 py-0.5 text-[9px] font-medium text-white">
									{tile.duration ?? "0:12"}
								</span>
							</>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

const PAIN_GAIN_ROWS: Array<{
	pain: { val: string; label: string };
	gain: { val: string; label: string };
}> = [
	{
		pain: { val: "4 hrs", label: "writing this week's posts" },
		gain: { val: "5 min", label: "telling your AI what to post" },
	},
	{
		pain: { val: "2 hrs", label: "reformatting for every network" },
		gain: { val: "0 hrs", label: "reformatting" },
	},
	{
		pain: { val: "1 hr", label: "cross-posting tab by tab" },
		gain: { val: "0 hrs", label: "cross-posting" },
	},
	{
		pain: { val: "1 hr", label: "scheduling each slot" },
		gain: { val: "0 hrs", label: "scheduling" },
	},
	{
		pain: { val: "1 hr", label: "switching between brand accounts" },
		gain: { val: "0 hrs", label: "switching" },
	},
	{
		pain: { val: "∞ hrs", label: "forgetting to post" },
		gain: { val: "0 hrs", label: "forgetting" },
	},
];

function PainColumn() {
	const cloudRainIconRef = useRef<CloudRainIconHandle>(null);

	return (
		<Reveal
			className="flex h-full flex-col gap-7 rounded-md px-5 py-6 sm:p-10"
			onMouseEnter={() => cloudRainIconRef.current?.startAnimation()}
			onMouseLeave={() => cloudRainIconRef.current?.stopAnimation()}
		>
			<div className="flex flex-col gap-2">
				<span className="inline-flex w-fit items-center rounded-full border border-border bg-muted px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
					Without AgentReacher
				</span>
				<h3 className="text-xl font-semibold tracking-[-0.02em] text-muted-foreground sm:text-2xl">
					Posting by hand
				</h3>
			</div>

			<ul className="flex flex-1 flex-col gap-3 text-base leading-relaxed sm:text-lg">
				{PAIN_GAIN_ROWS.map((row, i) => (
					<li
						key={row.pain.label}
						className="flex flex-wrap items-baseline gap-x-2"
					>
						{i > 0 && <span className="text-muted-foreground">+</span>}
						<span className="font-semibold text-red-600 line-through decoration-red-500/40 decoration-from-font dark:text-red-400">
							{row.pain.val}
						</span>
						<span className="text-muted-foreground">{row.pain.label}</span>
					</li>
				))}
			</ul>

			<div className="flex items-center gap-2.5 border-t border-border pt-6 text-base sm:text-lg">
				<span className="text-muted-foreground">=</span>
				<span className="font-semibold text-red-600 line-through decoration-red-500/40 decoration-from-font dark:text-red-400">
					10+ hrs
				</span>
				<span className="text-muted-foreground">of headaches / week</span>
				<CloudRainIcon
					ref={cloudRainIconRef}
					className="ml-auto size-6 shrink-0 text-muted-foreground"
					size={24}
					aria-hidden="true"
				/>
			</div>
		</Reveal>
	);
}

function GainColumn() {
	const sparklesIconRef = useRef<SparklesIconHandle>(null);

	return (
		<Reveal
			className="flex h-full flex-col gap-7 rounded-md px-5 py-6 sm:p-10"
			onMouseEnter={() => sparklesIconRef.current?.startAnimation()}
			onMouseLeave={() => sparklesIconRef.current?.stopAnimation()}
		>
			<div className="flex flex-col gap-2">
				<span className="inline-flex w-fit items-center rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700 dark:border-blue-400/30 dark:text-blue-300">
					With AgentReacher
				</span>
				<h3 className="text-xl font-semibold tracking-[-0.02em] sm:text-2xl">
					Posting on autopilot
				</h3>
			</div>

			<ul className="flex flex-1 flex-col gap-3 text-base leading-relaxed sm:text-lg">
				{PAIN_GAIN_ROWS.map((row, i) => (
					<li
						key={row.gain.label}
						className="flex flex-wrap items-baseline gap-x-2"
					>
						{i > 0 && <span className="text-muted-foreground">+</span>}
						<span className="font-semibold text-emerald-600 dark:text-emerald-400">
							{row.gain.val}
						</span>
						<span className="text-muted-foreground">{row.gain.label}</span>
					</li>
				))}
			</ul>

			<div className="flex items-center gap-2.5 border-t border-border pt-6 text-base sm:text-lg">
				<span className="text-muted-foreground">=</span>
				<span className="font-semibold text-emerald-600 dark:text-emerald-400">
					5 min
				</span>
				<span className="text-muted-foreground">of work / week</span>
				<SparklesIcon
					ref={sparklesIconRef}
					className="ml-auto size-6 shrink-0 text-muted-foreground"
					size={24}
					aria-hidden="true"
				/>
			</div>
		</Reveal>
	);
}

function PostCard({
	platform,
	title,
	body,
	time,
	status,
	surfaceClass,
}: {
	platform: "linkedin" | "instagram" | "x" | "facebook";
	title: string;
	body: string;
	time: string;
	status?: React.ReactNode;
	surfaceClass?: string;
}) {
	const platformConfig = {
		linkedin: {
			icon: <LinkedInIcon className="size-3 text-white" />,
			bg: "bg-[#0A66C2]",
			label: "LinkedIn",
		},
		instagram: {
			icon: <InstagramIcon className="size-3 text-white" />,
			bg: "bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]",
			label: "Instagram",
		},
		x: {
			icon: <XIcon className="size-2.5 text-white" />,
			bg: "bg-black",
			label: "X",
		},
		facebook: {
			icon: <FacebookIcon className="size-3 text-white" />,
			bg: "bg-[#1877F2]",
			label: "Facebook",
		},
	}[platform];

	return (
		<div
			className={`homepage-hover-cell rounded-lg border p-2 ${
				surfaceClass ?? "border-border bg-background"
			}`}
		>
			<div className="mb-1 flex items-center justify-between gap-2">
				{status}
				<span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
					{time}
				</span>
			</div>
			<div className="flex items-start gap-2">
				<div className="min-w-0 flex-1">
					<p className="truncate text-[12px] font-semibold leading-tight">
						{title}
					</p>
					<p className="line-clamp-1 text-[11px] text-muted-foreground">
						{body}
					</p>
					<div className="mt-1 inline-flex items-center gap-1 rounded-md border border-border bg-background/80 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
						<span
							className={`flex size-3 items-center justify-center rounded ${platformConfig.bg}`}
						>
							{platformConfig.icon}
						</span>
						{platformConfig.label}
					</div>
				</div>
			</div>
		</div>
	);
}

type AnimatedIconHandle = {
	startAnimation: () => void;
	stopAnimation: () => void;
};

type OutcomeIcon = React.ForwardRefExoticComponent<
	{
		className?: string;
		size?: number;
		"aria-hidden"?: boolean;
	} & React.RefAttributes<AnimatedIconHandle>
>;

const OUTCOMES: Array<{
	icon: OutcomeIcon;
	title: string;
	description: string;
}> = [
	{
		icon: RadioTowerIcon,
		title: "1 post, 9 networks",
		description:
			"Write the post once in chat. AgentReacher rewrites it for each platform's voice, picks the right time, and publishes across all nine.",
	},
	{
		icon: RefreshCWIcon,
		title: "From 2 posts a week to daily",
		description:
			"Daily posting used to mean daily work. Now AgentReacher drafts a week of posts at a time and ships them on schedule.",
	},
	{
		icon: TrendingUpIcon,
		title: "DMs, profile visits, signups",
		description:
			"Showing up every day puts you top of mind for people who can buy. They visit your profile, send DMs, and sign up without you chasing them.",
	},
];

function OutcomeCell({
	icon: Icon,
	title,
	description,
}: {
	icon: OutcomeIcon;
	title: string;
	description: string;
}) {
	const iconRef = useRef<AnimatedIconHandle>(null);
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: This hover handler only starts a decorative icon animation.
		<div
			className="relative flex h-full min-h-[190px] flex-col gap-3 px-5 py-6 sm:min-h-[250px] sm:gap-4 sm:px-8 sm:py-10"
			onMouseEnter={() => iconRef.current?.startAnimation()}
			onMouseLeave={() => iconRef.current?.stopAnimation()}
		>
			<div className="flex max-w-[30rem] flex-col">
				<div className="mb-3 flex size-8 items-center justify-center rounded-md text-foreground/75 transition-colors duration-200 group-hover:text-foreground sm:mb-5">
					<Icon ref={iconRef} size={20} aria-hidden={true} />
				</div>
				<h3 className="text-[18px] font-semibold leading-tight tracking-[-0.02em]">
					{title}
				</h3>
				<p
					className="mt-2 text-[15px] text-muted-foreground sm:mt-3"
					style={{ lineHeight: "1.62" }}
				>
					{description}
				</p>
			</div>
		</div>
	);
}

function ChannelsMockup() {
	const inner = PLATFORMS.slice(0, 4);
	const outer = PLATFORMS.slice(4);
	const orbitRef = useScrollOrbitSpeed();
	return (
		<div
			ref={orbitRef}
			className="relative flex h-[420px] size-full items-center justify-center overflow-clip px-8 py-12 sm:h-[500px] sm:px-12 sm:py-16 sm:overflow-x-clip"
		>
			<div className="relative flex size-full origin-center scale-[0.78] items-center justify-center sm:scale-100">
				<LogoIcon className="relative z-10 size-16" />
				<ul className="sr-only">
					{PLATFORMS.map((platform) => (
						<li key={platform.name}>{platform.name}</li>
					))}
				</ul>

				<OrbitingCircles radius={90} duration={28} iconSize={44} path reverse>
					{inner.map(({ name, icon: Icon, bg }) => (
						<div
							key={name}
							className={`flex size-11 items-center justify-center rounded-full text-white ${bg}`}
						>
							<Icon className="size-5" />
						</div>
					))}
				</OrbitingCircles>

				<OrbitingCircles radius={160} duration={55} iconSize={44} path>
					{outer.map(({ name, icon: Icon, bg }) => (
						<div
							key={name}
							className={`flex size-11 items-center justify-center rounded-full text-white ${bg}`}
						>
							<Icon className="size-5" />
						</div>
					))}
				</OrbitingCircles>
			</div>
		</div>
	);
}

function useScrollOrbitSpeed() {
	return useCallback((node: HTMLDivElement | null) => {
		if (!node) return;
		if (typeof window === "undefined") return;
		const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		if (reduceMotion.matches) return;

		let time = 0;
		let velocity = 0;
		let lastY = window.scrollY;
		let lastFrame = performance.now();
		let rafId = 0;
		let visible = true;

		const io =
			typeof IntersectionObserver !== "undefined"
				? new IntersectionObserver(
						([entry]) => {
							visible = Boolean(entry?.isIntersecting);
						},
						{ threshold: 0 },
					)
				: null;
		io?.observe(node);

		const onScroll = () => {
			const y = window.scrollY;
			if (visible) {
				velocity = Math.min(velocity + Math.abs(y - lastY), 70);
			}
			lastY = y;
		};

		const tick = (now: number) => {
			const dt = Math.min((now - lastFrame) / 1000, 0.05);
			lastFrame = now;
			velocity *= 0.18 ** dt; // half-life ~0.4s
			const boost = 1 + velocity * 0.05;
			if (visible) time += dt * boost;
			node.style.setProperty("--orbit-time", time.toFixed(3));
			rafId = window.requestAnimationFrame(tick);
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		rafId = window.requestAnimationFrame(tick);

		return () => {
			window.removeEventListener("scroll", onScroll);
			window.cancelAnimationFrame(rafId);
			io?.disconnect();
		};
	}, []);
}

function LinkedInIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		</svg>
	);
}

function XIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
		</svg>
	);
}

function BlueskyIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M5.202 2.857C7.954 4.922 10.913 9.11 12 11.358c1.087-2.247 4.046-6.436 6.798-8.501C20.783 1.366 24 .213 24 3.883c0 .732-.42 6.156-.667 7.037-.856 3.061-3.978 3.842-6.755 3.37 4.854.826 6.089 3.562 3.422 6.299-5.065 5.196-7.28-1.304-7.847-2.97-.104-.305-.152-.448-.153-.327 0-.121-.05.022-.153.327-.568 1.666-2.782 8.166-7.847 2.97-2.667-2.737-1.432-5.473 3.422-6.3-2.777.473-5.899-.308-6.755-3.369C.42 10.04 0 4.615 0 3.883c0-3.67 3.217-2.517 5.202-1.026z" />
		</svg>
	);
}

function InstagramIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
		</svg>
	);
}

function ThreadsIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z" />
		</svg>
	);
}

function FacebookIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
		</svg>
	);
}

function TikTokIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
		</svg>
	);
}

function YouTubeIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
		</svg>
	);
}

function PinterestIcon({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="24"
			height="24"
			fill="currentColor"
			className={className}
		>
			<path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24 18.635 24 24 18.633 24 12.013 24 5.393 18.635 0 12.017 0z" />
		</svg>
	);
}
